import type { ApolloClient, MutationOptions as ApolloMutationOptions, NormalizedCacheObject, OperationVariables, TypedDocumentNode } from "@apollo/client";
import type { DocumentNode, GraphQLError } from "graphql";
import type { DeepReadonly } from "../../util/type";
import type { Disposable } from "../../util/lifecycle";
import type { EventCallback, EventType } from "../../util/event-manager";
import type { ApiService } from "./api-service";
import { EventManager } from "../../util/event-manager";
import { UnhandledGraphQLError } from "../../util/error";
import { notNullish } from "../../util/nullish";


export type MutationEvents<Data, Variables extends OperationVariables> = {
	loading: {
		mutation: Mutation<Data, Variables>;
	},
	data: {
		data: DeepReadonly<Data>,
		mutation: Mutation<Data, Variables>;
	},
	error: {
		error: GraphQLError;
		mutation: Mutation<Data, Variables>;
	};
};

export type MutationRefetchQueries = DocumentNode[];
export type MutationOptions<Data, Variables extends OperationVariables = OperationVariables> = {
	mutation: TypedDocumentNode<Data, Variables>;
	refetchQueries?: MutationRefetchQueries;
};

export class Mutation<Data, Variables extends OperationVariables = OperationVariables> implements Disposable {
	readonly #api: ApiService;
	readonly #client: ApolloClient<NormalizedCacheObject>;
	readonly #mutation: TypedDocumentNode<Data, Variables>;
	readonly #refetchQueries: ApolloMutationOptions<Data, Variables>["refetchQueries"];

	readonly #events = new EventManager<MutationEvents<Data, Variables>>();

	constructor(api: ApiService, client: ApolloClient<NormalizedCacheObject>, options: Readonly<MutationOptions<Data, Variables>>) {
		this.#api = api;
		this.#client = client;
		this.#mutation = options.mutation;
		this.#refetchQueries = options.refetchQueries;
	}

	/**
	 * Submit the operation with the passed variables.
	 *
	 * @returns the result of the operation, or `null` if the response included
	 * 	errors, but they were all handled.
	 */
	async submit(variables: Variables): Promise<Data | null> {
		this.#events.notify("loading", { mutation: this });
		const result = await this.#client.mutate({
			mutation: this.#mutation,
			variables,
			errorPolicy: "all",
			refetchQueries: this.#refetchQueries,
			awaitRefetchQueries: true,
		});

		if(result.errors) {
			for(const error of result.errors) {
				const result = await this.#api["handleError"](error);
				if(!result.handled) {
					const handledByEvent = this.#events.notify("error", { error, mutation: this });
					if(handledByEvent) {
						return null;
					}

					throw new UnhandledGraphQLError(error);
				}

				if(result.retry) {
					return await this.submit(variables);
				}
			}

			return null;
		}

		if(notNullish(result.data)) {
			this.#events.notify("data", {
				data: result.data,
				mutation: this,
			});
			return result.data;
		}

		throw new Error("No data returned from mutation");
	}

	/**
	 * Submit the operation with the passed variables.
	 *
	 * @returns the result of the operation.
	 * @throws if the result of the operation is `null`.
	 */
	async submitOrThrow(variables: Variables): Promise<Data> {
		const result = await this.submit(variables);
		if(!result) {
			throw new Error("Mutation resulted in null after error-handling.");
		}

		return result;
	}

	on<E extends EventType<MutationEvents<Data, Variables>>>(event: E, listener: EventCallback<MutationEvents<Data, Variables>, E>): this {
		this.#events.on(event, listener);
		return this;
	}

	off<E extends EventType<MutationEvents<Data, Variables>>>(event: E, listener: EventCallback<MutationEvents<Data, Variables>, E>): this {
		this.#events.off(event, listener);
		return this;
	}

	dispose(): void {
		this.#events.dispose();
	}
}
